import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, RotateCcw, Trophy, Pause, Play } from "lucide-react";
import { api } from "@/lib/api";
import { sfx } from "../citySfx";

// Keyboard → action map
const KEYMAP = {
  ArrowLeft: "left", KeyA: "left",
  ArrowRight: "right", KeyD: "right",
  ArrowUp: "up", KeyW: "up",
  ArrowDown: "down", KeyS: "down",
  Space: "a", Enter: "a", KeyZ: "a", KeyK: "a",
  KeyX: "b", ShiftLeft: "b", KeyL: "b",
};

const CTRL_LABEL = { left: "◄", right: "►", up: "▲", down: "▼", a: "A", b: "B" };

export default function GameHost({ game, playerName, onExit, onCoin }) {
  const wrapRef = useRef(null);
  const surfaceRef = useRef(null); // canvas (2d) or div (3d)
  const gameRef = useRef(null);
  const rafRef = useRef(0);
  const inputRef = useRef({ held: {}, taps: [] });
  const [score, setScore] = useState(0);
  const [over, setOver] = useState(false);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  const overRef = useRef(false);
  const [board, setBoard] = useState([]);
  const [name, setName] = useState(playerName || "");
  const [submitted, setSubmitted] = useState(false);
  const [earned, setEarned] = useState(0);
  const [restartKey, setRestartKey] = useState(0);

  const loadBoard = () => api.get(`/arcade/scores?game=${game.id}`).then(({ data }) => setBoard(data.scores || [])).catch(() => {});

  // Boot / reboot the game
  useEffect(() => {
    overRef.current = false; pausedRef.current = false;
    setOver(false); setPaused(false); setScore(0); setSubmitted(false); setEarned(0);
    inputRef.current = { held: {}, taps: [] };
    const host = { input: inputRef.current, sfx, W: game.w, H: game.h };
    let g;
    if (game.dim === "3d") {
      const div = surfaceRef.current; div.innerHTML = "";
      host.container = div;
      g = game.create(host);
    } else {
      const canvas = surfaceRef.current;
      canvas.width = game.w; canvas.height = game.h;
      const ctx = canvas.getContext("2d");
      host.canvas = canvas; host.ctx = ctx;
      g = game.create(host);
    }
    gameRef.current = g;
    let last = performance.now();
    const loop = (t) => {
      rafRef.current = requestAnimationFrame(loop);
      const dt = Math.min(0.05, (t - last) / 1000); last = t;
      if (!pausedRef.current && !overRef.current) {
        g.frame(dt);
        setScore((s) => (s !== g.score ? g.score : s));
        if (g.over) {
          overRef.current = true;
          setScore(g.score);
          const coins = Math.max(0, Math.min(60, Math.floor(g.score / (game.coinPer || 50))));
          setEarned(coins);
          if (coins > 0) onCoin?.(coins, game.name);
          sfx.win?.();
          setOver(true);
          loadBoard();
        }
      }
      inputRef.current.taps.length = 0; // taps live exactly one processed frame
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(rafRef.current); try { g.destroy?.(); } catch {} };
    // eslint-disable-line react-hooks/exhaustive-deps
  }, [restartKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard
  useEffect(() => {
    const down = (e) => {
      const a = KEYMAP[e.code];
      if (!a) return;
      e.preventDefault();
      if (!e.repeat) { inputRef.current.held[a] = true; inputRef.current.taps.push(a); }
    };
    const up = (e) => { const a = KEYMAP[e.code]; if (a) inputRef.current.held[a] = false; };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, []);

  const restart = () => { sfx.click?.(); setRestartKey((k) => k + 1); };
  const togglePause = () => { pausedRef.current = !pausedRef.current; setPaused(pausedRef.current); };

  // Touch control handlers
  const press = (a, on) => (e) => {
    e.preventDefault(); e.stopPropagation();
    inputRef.current.held[a] = on;
    if (on) inputRef.current.taps.push(a);
  };
  const controls = game.controls || ["left", "right", "up", "down", "a", "b"];
  const hasDir = controls.some((c) => ["left", "right", "up", "down"].includes(c));

  const submit = async () => {
    const nm = (name || "DUCK").trim().slice(0, 12);
    try { await api.post("/arcade/scores", { game: game.id, name: nm, score }); setSubmitted(true); loadBoard(); try { localStorage.setItem("cq_arcade_name", nm); } catch {} } catch {}
  };
  useEffect(() => { try { const n = localStorage.getItem("cq_arcade_name"); if (n && !name) setName(n); } catch {} }, []); // eslint-disable-line

  const aspect = (game.w || 3) / (game.h || 4);

  return (
    <div ref={wrapRef} className="flex flex-col h-full w-full" data-testid={`arcade-game-${game.id}`}>
      {/* Top bar */}
      <div className="flex items-center gap-2 px-2 py-2 shrink-0">
        <button onClick={() => { sfx.close?.(); onExit(); }} className="cq-px text-[9px] px-3 py-2 border border-white/20 text-slate-200 hover:bg-white/10 inline-flex items-center gap-1" data-testid="arcade-back-lobby"><ChevronLeft size={12} /> LOBBY</button>
        <div className="flex-1 text-center">
          <p className="cq-px text-cyan-300 text-[10px] leading-none">{game.name}</p>
          <p className="cq-px text-amber-300 text-[8px] mt-1">SCORE {score}</p>
        </div>
        <button onClick={togglePause} className="cq-px text-[9px] px-3 py-2 border border-white/20 text-slate-200 hover:bg-white/10" data-testid="arcade-pause">{paused ? <Play size={12} /> : <Pause size={12} />}</button>
        <button onClick={restart} className="cq-px text-[9px] px-3 py-2 border border-white/20 text-slate-200 hover:bg-white/10" data-testid="arcade-restart"><RotateCcw size={12} /></button>
      </div>

      {/* Play surface */}
      <div className="relative flex-1 min-h-0 grid place-items-center bg-black/40 overflow-hidden">
        <div className="relative" style={{ width: "min(100%, calc((100vh - 210px) * " + aspect + "))", aspectRatio: `${game.w} / ${game.h}`, maxHeight: "100%" }}>
          {game.dim === "3d"
            ? <div ref={surfaceRef} className="absolute inset-0 w-full h-full" style={{ imageRendering: "pixelated" }} />
            : <canvas ref={surfaceRef} className="absolute inset-0 w-full h-full" style={{ imageRendering: "pixelated" }} />}
          {paused && !over && <div className="absolute inset-0 grid place-items-center bg-black/60"><p className="cq-px text-cyan-300 text-sm">PAUSED</p></div>}

          {over && (
            <div className="absolute inset-0 grid place-items-center bg-black/80 p-4" data-testid="arcade-gameover">
              <div className="w-full max-w-xs text-center">
                <p className="cq-px text-rose-400 text-sm">GAME OVER</p>
                <p className="cq-px text-white text-lg mt-3">{score}</p>
                {earned > 0 && <p className="cq-px text-amber-300 text-[9px] mt-2">+{earned} COINS EARNED</p>}
                {!submitted ? (
                  <div className="mt-4">
                    <input value={name} onChange={(e) => setName(e.target.value.toUpperCase())} maxLength={12} placeholder="YOUR NAME" className="w-full bg-white/10 border border-white/20 text-center cq-px text-[9px] text-white px-2 py-2 outline-none focus:border-cyan-400" data-testid="arcade-name" />
                    <button onClick={submit} className="mt-2 w-full cq-px text-[9px] bg-cyan-400 text-slate-950 py-2 hover:bg-cyan-300" data-testid="arcade-submit-score">SAVE TO LEADERBOARD</button>
                  </div>
                ) : <p className="cq-px text-emerald-300 text-[9px] mt-3" data-testid="arcade-saved">✓ SAVED</p>}
                {board.length > 0 && (
                  <div className="mt-4 text-left border border-white/10 bg-white/5 p-2" data-testid="arcade-leaderboard">
                    <p className="cq-px text-[8px] text-amber-300 mb-1 flex items-center gap-1"><Trophy size={10} /> TOP DUCKS</p>
                    {board.slice(0, 5).map((s, i) => <div key={i} className="flex justify-between cq-px text-[8px] text-slate-300"><span>{i + 1}. {s.name}</span><span className="text-cyan-300">{s.score}</span></div>)}
                  </div>
                )}
                <div className="mt-3 flex gap-2">
                  <button onClick={restart} className="flex-1 cq-px text-[9px] bg-amber-400 text-slate-950 py-2 hover:bg-amber-300" data-testid="arcade-playagain">PLAY AGAIN</button>
                  <button onClick={onExit} className="flex-1 cq-px text-[9px] border border-white/20 text-slate-200 py-2 hover:bg-white/10">LOBBY</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Touch controls */}
      <div className="shrink-0 flex items-end justify-between px-4 py-3 select-none touch-none" style={{ minHeight: 96 }} data-testid="arcade-touch">
        {hasDir ? (
          <div className="relative w-32 h-32">
            {controls.includes("up") && <button onPointerDown={press("up", true)} onPointerUp={press("up", false)} onPointerLeave={press("up", false)} className="absolute left-1/2 -translate-x-1/2 top-0 w-11 h-11 rounded bg-white/10 active:bg-cyan-400/60 text-white grid place-items-center">{CTRL_LABEL.up}</button>}
            {controls.includes("down") && <button onPointerDown={press("down", true)} onPointerUp={press("down", false)} onPointerLeave={press("down", false)} className="absolute left-1/2 -translate-x-1/2 bottom-0 w-11 h-11 rounded bg-white/10 active:bg-cyan-400/60 text-white grid place-items-center">{CTRL_LABEL.down}</button>}
            {controls.includes("left") && <button onPointerDown={press("left", true)} onPointerUp={press("left", false)} onPointerLeave={press("left", false)} className="absolute left-0 top-1/2 -translate-y-1/2 w-11 h-11 rounded bg-white/10 active:bg-cyan-400/60 text-white grid place-items-center">{CTRL_LABEL.left}</button>}
            {controls.includes("right") && <button onPointerDown={press("right", true)} onPointerUp={press("right", false)} onPointerLeave={press("right", false)} className="absolute right-0 top-1/2 -translate-y-1/2 w-11 h-11 rounded bg-white/10 active:bg-cyan-400/60 text-white grid place-items-center">{CTRL_LABEL.right}</button>}
          </div>
        ) : <div className="w-32" />}
        <div className="flex gap-3">
          {controls.includes("b") && <button onPointerDown={press("b", true)} onPointerUp={press("b", false)} onPointerLeave={press("b", false)} className="w-16 h-16 rounded-full bg-rose-500/30 border border-rose-400/60 active:bg-rose-400/70 text-white cq-px text-[10px] grid place-items-center">B</button>}
          {controls.includes("a") && <button onPointerDown={press("a", true)} onPointerUp={press("a", false)} onPointerLeave={press("a", false)} className="w-16 h-16 rounded-full bg-cyan-500/30 border border-cyan-400/60 active:bg-cyan-400/70 text-white cq-px text-[10px] grid place-items-center">A</button>}
        </div>
      </div>
    </div>
  );
}
